<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useNotificationStore } from '@/stores/notifications'
import { useToastStore } from '@/stores/toast'
import { formatRelative } from '@/composables/useFormatters'
import { useRealtime } from '@/composables/useRealtime'

const store = useNotificationStore()
const toast = useToastStore()
const router = useRouter()
const { recent, unreadCount, loading } = storeToRefs(store)

const open = ref(false)
const container = ref<HTMLElement | null>(null)

async function toggle(): Promise<void> {
  open.value = !open.value
  if (open.value) {
    await Promise.all([store.loadRecent(), store.refreshUnreadCount()])
  }
}

function onClickOutside(event: MouseEvent): void {
  if (open.value && container.value && !container.value.contains(event.target as Node)) {
    open.value = false
  }
}

async function markAll(): Promise<void> {
  const marked = await store.markAllRead()
  toast.success(marked > 0 ? `Označeno ${marked} notifikací jako přečtené.` : 'Vše už bylo přečtené.')
}

function goToDetail(): void {
  open.value = false
  void router.push('/notifications')
}

// Nová notifikace se projeví v počítadle i v otevřeném panelu bez obnovení.
useRealtime(['notification.created', 'notification.read'], () => {
  void store.refreshUnreadCount()
  if (open.value) {
    void store.loadRecent()
  }
})

onMounted(() => document.addEventListener('mousedown', onClickOutside))
onBeforeUnmount(() => document.removeEventListener('mousedown', onClickOutside))
</script>

<template>
  <div
    ref="container"
    class="bell"
  >
    <button
      type="button"
      class="bell__trigger"
      :aria-expanded="open"
      aria-haspopup="true"
      :aria-label="`Notifikace${unreadCount ? `, ${unreadCount} nepřečtených` : ''}`"
      @click="toggle"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M18 15V10a6 6 0 10-12 0v5l-2 3h16l-2-3zM10 21h4"
          stroke="currentColor"
          stroke-width="1.7"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <span
        v-if="unreadCount > 0"
        class="bell__badge"
      >{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
    </button>

    <Transition name="pop">
      <div
        v-if="open"
        class="bell__panel"
      >
        <header class="bell__header">
          <strong>Notifikace</strong>
          <button
            v-if="unreadCount > 0"
            type="button"
            class="bell__action"
            @click="markAll"
          >
            Označit vše
          </button>
        </header>

        <div
          v-if="loading"
          class="bell__state muted"
        >
          Načítám…
        </div>
        <div
          v-else-if="recent.length === 0"
          class="bell__state muted"
        >
          Žádné notifikace.
        </div>

        <ul
          v-else
          class="bell__list"
        >
          <li
            v-for="item in recent"
            :key="item.id"
            class="bell__item"
            :class="{ 'is-unread': !item.read }"
          >
            <span
              class="bell__dot"
              :class="`bell__dot--${item.level}`"
              aria-hidden="true"
            />
            <div class="bell__content">
              <strong class="bell__title">{{ item.title }}</strong>
              <p class="bell__message truncate">
                {{ item.message }}
              </p>
              <time class="bell__time">{{ formatRelative(item.createdAt) }}</time>
            </div>
            <button
              v-if="!item.read"
              type="button"
              class="bell__action"
              @click="store.markRead(item.id, true)"
            >
              Přečteno
            </button>
          </li>
        </ul>

        <footer class="bell__footer">
          <button
            type="button"
            class="bell__action"
            @click="goToDetail"
          >
            Zobrazit všechny
          </button>
        </footer>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.bell { position: relative; }

.bell__trigger {
  position: relative;
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  background: var(--surface-card);
  color: var(--text-muted);
  cursor: pointer;
  transition: background var(--transition), color var(--transition);
}

.bell__trigger:hover { background: var(--surface-hover); color: var(--text-strong); }

.bell__badge {
  position: absolute;
  top: -5px;
  right: -5px;
  min-width: 17px;
  padding: 0 4px;
  border-radius: var(--radius-full);
  background: var(--danger);
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  line-height: 17px;
  text-align: center;
}

.bell__panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: min(360px, calc(100vw - 32px));
  background: var(--surface-raised);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  z-index: 50;
}

.bell__header,
.bell__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px var(--space-4);
}

.bell__header { border-bottom: 1px solid var(--border-subtle); }
.bell__footer { border-top: 1px solid var(--border-subtle); }

.bell__action {
  padding: 0;
  border: none;
  background: none;
  color: var(--accent);
  font-size: 12.5px;
  font-weight: 550;
  cursor: pointer;
}

.bell__action:hover { text-decoration: underline; }

.bell__state { padding: var(--space-5); text-align: center; font-size: 13px; }

.bell__list { max-height: 340px; margin: 0; padding: 0; list-style: none; overflow-y: auto; }

.bell__item {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: 11px var(--space-4);
  border-bottom: 1px solid var(--border-subtle);
}

.bell__item:last-child { border-bottom: none; }
.bell__item.is-unread { background: var(--accent-soft); }

.bell__dot {
  flex-shrink: 0;
  width: 7px;
  height: 7px;
  margin-top: 6px;
  border-radius: 50%;
  background: var(--text-muted);
}

.bell__dot--success { background: var(--success); }
.bell__dot--warning { background: var(--warning); }
.bell__dot--error { background: var(--danger); }
.bell__dot--info { background: var(--info); }

.bell__content { flex: 1; min-width: 0; }
.bell__title { display: block; font-size: 13px; color: var(--text-strong); }
.bell__message { font-size: 12.5px; color: var(--text-muted); }
.bell__time { font-size: 11.5px; color: var(--text-muted); }

.pop-enter-active, .pop-leave-active { transition: opacity var(--transition), transform var(--transition); }
.pop-enter-from, .pop-leave-to { opacity: 0; transform: translateY(-4px); }
</style>
